import React, { useEffect } from "react";
import { API_BASE_URL, companyWebsite } from "../../config";
import {
  Card,
  Button,
  Row,
  Col,
  Typography,
  Divider,
  Space,
  Alert,
  Spin,
} from "antd";
import {
  UserOutlined,
  LoginOutlined,
  ClockCircleOutlined,
  PhoneOutlined,
  EnvironmentOutlined,
  MailOutlined,
  CalendarOutlined,
} from "@ant-design/icons";
import PublicIcon from "@mui/icons-material/Public";
import BackgroundImage from "../../assets/ais.png";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchAdmissionPeriod } from "../../store/admissionPeriodSlice";
import { fetchBatches } from "../../store/AdmissionInformationSlice";
import useTokenExpiryCheck from "../../utils/UseTokenExpiryCheck";

const { Title, Text, Paragraph } = Typography;

// Helper function to format dates
const formatDate = (dateString) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
};

const AdmissionInformationPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { validatedBatchesForExistingParent, validatedBatchesForNewParent } =
    useSelector((state) => state.admissionInformation.batches);

  const { loading, error } = useSelector((state) => state.admissionInformation);

  const { existingParentWindow, newParentWindow } = useSelector(
    (state) => state.admissionPeriod
  );
console.log(
  "existingParentWindow, newParentWindow",
  existingParentWindow,
  newParentWindow
);
  const { isAuthenticated } = useSelector((state) => state.auth);

  useEffect(() => {
    const fetchBatch = async () => {
      try {
        await dispatch(fetchBatches()).unwrap();
      } catch (err) {
        console.error("Failed to fetch batch", err);
      }
    };
    fetchBatch();
  }, [dispatch]);

  useEffect(() => {
    const fetchPeriod = async () => {
      try {
        await dispatch(fetchAdmissionPeriod()).unwrap();
      } catch (err) {
        console.error("Failed to fetch admission period", err);
      }
    };

    fetchPeriod();
  }, []);

  useTokenExpiryCheck();

  // Fallback to open admission if there's an error or no data
  const admissionStatus =
    error || (!existingParentWindow && !newParentWindow)
      ? false
      : existingParentWindow?.isOpen || newParentWindow?.isOpen;

   if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Spin
          size="large"
          tip="Loading admission information..."
          className="text-blue-600"
        />
      </div>
    );
  }
 
  const message = {
    "admission window": error ? "doesn't exist" : "exist", // Removed the extra colon
    "open batches for existing parent":
      validatedBatchesForExistingParent?.length || 0, // Corrected spelling
    "open batches for new parent": validatedBatchesForNewParent?.length || 0, // Corrected spelling
  };

  // Handle error state
  if (
    error ||
    (validatedBatchesForExistingParent.length === 0 &&
      validatedBatchesForNewParent.length === 0)
  ) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Card className="w-full max-w-md shadow-lg">
          <div className="text-center p-6">
            <div className="bg-red-100 rounded-full w-20 h-20 mx-auto mb-4 flex items-center justify-center">
              <ClockCircleOutlined className="text-4xl text-red-600" />
            </div>
            <Title level={3} className="text-red-600 mb-4">
              Admission Information Unavailable
            </Title>
            <Paragraph className="text-gray-600 mb-6">
              We regret to inform you that we are not accepting admissions at
              this time. Please check back later or reach out to our admissions
              office for further details. You can find more information on our
              website by clicking the link below.
            </Paragraph>
            <Alert
              message="Information"
              description={JSON.stringify(message, null, 2)}
              type="info"
              showIcon
              className="mb-4"
            />
            <Button
              onClick={() => {
                window.open(companyWebsite, "_blank");
              }}
              style={{
                backgroundColor: "#2D6137", // Tomato color for background
                color: "#FFFFFF", // White text for contrast
                transition: "background-color 0.3s ease",
              }}
              className="w-full flex items-center justify-center hover:bg-[#FF4500]" // Darker orange on hover
            >
              <PublicIcon className="mr-2" />
              Our website
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  // Helper function to handle new applicants navigation
  const handlePreAdmissionNavigation = (isNewParent) => {
    if (isNewParent) {
      // For new parents, always go to /application-form with unauthenticated route
      navigate("/application-enquiry-newparent", {
        state: {
          preAdmissionType: "unauthenticated",
          apiEndpoint: `${API_BASE_URL}/api/ApplicantStudent/Unauthenticated`,
        },
      });
    } else {
      // For existing parents
      if (isAuthenticated) {
        // If already logged in, go to /new-applicant-list with authenticated route
        navigate("/application-form", {
          state: {
            preAdmissionType: "authenticated",
            apiEndpoint: `${API_BASE_URL}/api/ApplicantStudent/Authenticated`,
          },
        });
      } else {
        // If not logged in, redirect to login first
        navigate("/login", {
          state: {
            redirectTo: "/application-form",
            preAdmissionType: "authenticated",
            apiEndpoint: `${API_BASE_URL}/api/ApplicantStudent/Authenticated`,
          },
        });
      }
    }
  };

  const registrarInfo = {
    name: "Dr. Sarah Johnson",
    position: "Head of Registrar Office",
    office: "Main Building, 2nd Floor, Room 205",
    telephone: "+251-911-123-456",
    email: "registrar@school.edu.et",
  };

  const officeHours = [
    "Monday - Friday: 8:00 AM - 5:00 PM",
    "Saturday: 8:00 AM - 12:00 PM",
    "Sunday: Closed",
  ];

  return (
    <div
      className="min-h-screen relative bg-cover bg-center bg-no-repeat"
      style={{
        backgroundImage: `url(${BackgroundImage})`,
        backgroundBlendMode: "overlay",
        backgroundColor: "rgba(255, 255, 255, 0.9)",
      }}
    >
      <div className="container mx-auto px-4 py-8">
        {/* Admission Period Image */}
        {(existingParentWindow || newParentWindow) && (
          <div className="max-w-full mx-auto mb-8 relative px-4">
            <div
              className="w-full h-48 bg-cover bg-center rounded-lg shadow-lg overflow-hidden flex items-center justify-center"
              style={{
                backgroundImage: `linear-gradient(to right, rgba(0,0,0,0.7), rgba(0,0,0,0.5)), url(${BackgroundImage})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
              }}
            >
              <div className="text-center text-white px-6">
                <Title
                  level={3}
                  className="mb-2"
                  style={{
                    color: "white !important",
                    textAlign: "center",
                    margin: "0 0 10px 0",
                  }}
                >
                  <span style={{ color: "white" }}>
                    {admissionStatus
                      ? "Admission Period Open"
                      : "Admission Period Closed"}
                  </span>
                </Title>
                <div className="flex justify-center space-x-8">
                  {existingParentWindow && (
                    <div>
                      <Text className="bg-blue-600 text-white opacity-80 block p-2 rounded">
                        Existing Parents{" "}
                        <span className="text-red-600 font-semibold">
                          (parent role login)
                        </span>
                      </Text>
                      <Text className="text-white text-xl font-bold">
                        {formatDate(existingParentWindow.startDate)} to{" "}
                        {formatDate(existingParentWindow.endDate)}
                      </Text>
                    </div>
                  )}
                  {newParentWindow && (
                    <div>
                      <Text className="text-white opacity-80 block">
                        New Parents
                      </Text>
                      <Text className="text-white text-xl font-bold">
                        {formatDate(newParentWindow.startDate)} to{" "}
                        {formatDate(newParentWindow.endDate)}
                      </Text>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Main Title */}
        <Title level={2} className="text-center mb-8 text-blue-900 px-4">
          School Admission Information
        </Title>

        {admissionStatus ? (
          <Row gutter={[24, 24]} className="max-w-full mx-auto mb-8 px-4">
            {/* Existing Parents Card */}
            {existingParentWindow && (
              <Col
                xs={24}
                md={existingParentWindow && newParentWindow ? 12 : 24}
              >
                <Card
                  hoverable
                  className="h-full shadow-lg border-2 border-blue-100 transition-all duration-300 hover:shadow-xl"
                  title={
                    <div className="flex items-center gap-3">
                      <LoginOutlined className="text-blue-600" />
                      <span className="text-lg">Existing Parents</span>
                    </div>
                  }
                >
                  <div className="text-center">
                    <div className="bg-blue-50 rounded-full w-20 h-20 mx-auto mb-6 flex items-center justify-center">
                      <LoginOutlined className="text-4xl text-blue-600" />
                    </div>
                    <Title level={4}>Already Have an Account?</Title>
                    <Paragraph className="mb-6 text-gray-600">
                      {`Admission period open for existing parents from ${formatDate(
                        existingParentWindow.startDate
                      )} to ${formatDate(existingParentWindow.endDate)}`}
                    </Paragraph>
                    <Button
                      type="primary"
                      size="large"
                      icon={<LoginOutlined />}
                      onClick={() => handlePreAdmissionNavigation(false)}
                      className="w-full h-12"
                    >
                      Login to Continue
                    </Button>
                  </div>
                </Card>
              </Col>
            )}

            {/* New Parents Card */}
            {newParentWindow && (
              <Col
                xs={24}
                md={existingParentWindow && newParentWindow ? 12 : 24}
              >
                <Card
                  hoverable
                  className="h-full shadow-lg border-2 border-green-100 transition-all duration-300 hover:shadow-xl"
                  title={
                    <div className="flex items-center gap-3">
                      <UserOutlined className="text-green-600" />
                      <span className="text-lg">New Parents</span>
                    </div>
                  }
                >
                  <div className="text-center">
                    <div className="bg-green-50 rounded-full w-20 h-20 mx-auto mb-6 flex items-center justify-center">
                      <UserOutlined className="text-4xl text-green-600" />
                    </div>
                    <Title level={4}>New to Our School?</Title>
                    <Paragraph className="mb-6 text-gray-600">
                      {`Admission period open for new parents from ${formatDate(
                        newParentWindow.startDate
                      )} to ${formatDate(newParentWindow.endDate)}`}
                    </Paragraph>
                    <Button
                      type="primary"
                      size="large"
                      icon={<UserOutlined />}
                      onClick={() => handlePreAdmissionNavigation(true)}
                      className="w-full h-12"
                      style={{
                        backgroundColor: "#52c41a",
                        borderColor: "#52c41a",
                      }}
                    >
                      Start Application
                    </Button>
                  </div>
                </Card>
              </Col>
            )}
          </Row>
        ) : (
          /* Admission Closed Message */
          <div className="max-w-full mx-auto mb-8 px-4">
            <Card className="shadow-lg border-2 border-gray-200 bg-white">
              <div className="text-center py-8">
                <div className="bg-gray-100 rounded-full w-20 h-20 mx-auto mb-6 flex items-center justify-center">
                  <ClockCircleOutlined className="text-4xl text-gray-400" />
                </div>
                <Title level={3}>Admission is Currently Closed</Title>
                <Paragraph className="text-gray-600 mb-6">
                  No admission windows are currently open. Please check back
                  during the next admission period or contact our office for
                  more information.
                </Paragraph>
                {(newParentWindow || existingParentWindow) && (
                  <Alert
                    message="Upcoming Admission Periods"
                    description={
                      <>
                        {newParentWindow && (
                          <div>
                            New Parents: {formatDate(newParentWindow.startDate)}{" "}
                            to {formatDate(newParentWindow.endDate)}
                          </div>
                        )}
                        {existingParentWindow && (
                          <div>
                            Existing Parents:{" "}
                            {formatDate(existingParentWindow.startDate)} to{" "}
                            {formatDate(existingParentWindow.endDate)}
                          </div>
                        )}
                      </>
                    }
                    type="info"
                    showIcon
                  />
                )}
              </div>
            </Card>
          </div>
        )}

        {/* Registrar Office Information */}
        <Row gutter={[24, 24]} className="max-w-full mx-auto px-4">
          <Col xs={24} md={12}>
            <Card
              className="shadow-lg border-2 border-blue-100"
              title={
                <div className="flex items-center gap-3">
                  <svg
                    className="w-5 h-5 text-blue-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                    />
                  </svg>
                  <span className="text-lg">Registrar Office Services</span>
                </div>
              }
            >
              <Space direction="vertical" size="middle" className="w-full">
                <Paragraph className="text-gray-600">
                  Our Registrar Office provides the following services:
                  <ul className="list-disc ml-8 mt-2">
                    <li>Admission processing and guidance</li>
                    <li>Document verification</li>
                    <li>Student records management</li>
                    <li>Transcript requests</li>
                    <li>Enrollment verification</li>
                    <li>Academic calendar information</li>
                  </ul>
                </Paragraph>
                <div>
                  <Title level={4} className="flex items-center gap-2">
                    <ClockCircleOutlined className="text-blue-600" /> Office
                    Hours
                  </Title>
                  {officeHours.map((hours, index) => (
                    <Paragraph key={index} className="mb-1 text-gray-600">
                      {hours}
                    </Paragraph>
                  ))}
                </div>
              </Space>
            </Card>
          </Col>
          <Col xs={24} md={12}>
            <Card
              className="shadow-lg border-2 border-blue-100"
              title={
                <div className="flex items-center gap-3">
                  <PhoneOutlined className="text-blue-600" />
                  <span className="text-lg">Contact Information</span>
                </div>
              }
            >
              <Space direction="vertical" size="middle" className="w-full">
                <div>
                  <Title level={4}>{registrarInfo.name}</Title>
                  <Text type="secondary">{registrarInfo.position}</Text>
                </div>
                <Space direction="vertical" className="w-full">
                  <Text className="flex items-center gap-2 text-gray-600">
                    <EnvironmentOutlined className="text-blue-600" />
                    {registrarInfo.office}
                  </Text>
                  <Text className="flex items-center gap-2 text-gray-600">
                    <PhoneOutlined className="text-blue-600" />
                    {registrarInfo.telephone}
                  </Text>
                  <Text className="flex items-center gap-2 text-gray-600">
                    <MailOutlined className="text-blue-600" />
                    {registrarInfo.email}
                  </Text>
                </Space>
                <Divider />
                <div>
                  <Title level={4}>Additional Contact Methods</Title>
                  <Space direction="vertical" className="w-full">
                    <Text className="text-gray-600">Fax: +251-111-234-567</Text>
                    <Text className="text-gray-600">
                      P.O. Box: 1234, Addis Ababa, Ethiopia
                    </Text>
                    <Text className="text-gray-600">
                      Emergency Contact: +251-911-987-654
                    </Text>
                  </Space>
                </div>
              </Space>
            </Card>
          </Col>
        </Row>
      </div>
    </div>
  );
};

export default AdmissionInformationPage;
